import { useMemo } from "react";

import { hojeBrasilia } from "@/modules/obras/hoje-brasilia";
import { useObraEmbarcacaoInformacoes, useObraLimitesClasse } from "@/modules/obras/use-obra-query";
import { avaliarCondicoesObra, SEM_CONDICOES } from "./nova-obra.condicoes";
import type { CondicoesObra, PeriodoCondicoes } from "./nova-obra.condicoes";
import { useNovaObra } from "./nova-obra.context";
import { periodoDaObraNoWizard } from "./nova-obra.equipe";
import { readTiposObra } from "./nova-obra.tipos";

/**
 * Situação da avaliação. Só `ok` permite afirmar "sem pendência": em `carregando`/`erro` as
 * condições vêm vazias por falta de dado, não por estarem resolvidas (quem cria a obra, HU 2543,
 * deve tratar). Nada disso bloqueia a navegação (D-088).
 */
export type StatusCondicoesObra = "carregando" | "erro" | "ok";

export interface CondicoesObraWizard {
  status: StatusCondicoesObra;
  /** `status === "ok"`. */
  pronto: boolean;
  /** Período consolidado (Início da Obra → Término previsto); `null` antes de haver período (RN03). */
  periodo: PeriodoCondicoes | null;
  hoje: string;
  condicoes: CondicoesObra;
}

/**
 * HU 2549: avalia, com o estado do wizard, os limites de classe e o contrato da embarcação
 * (mesma regra do hub-core). Sem período ou sem limites carregados, não há pendência calculada.
 */
export function useCondicoesObra(): CondicoesObraWizard {
  const { state } = useNovaObra();
  const embarcacaoId = state.obra.embarcacaoId;
  const limites = useObraLimitesClasse(embarcacaoId);
  const informacoes = useObraEmbarcacaoInformacoes(embarcacaoId);

  const periodo = useMemo(
    () => periodoDaObraNoWizard(state.etapas.duracao),
    [state.etapas.duracao],
  );
  const tipos = state.etapas.tiposObra;
  const hoje = useMemo(() => hojeBrasilia(), []);
  const consultas = [limites.status, informacoes.status];
  const status: StatusCondicoesObra = consultas.some(
    (consulta) => consulta === "error" || consulta === "forbidden",
  )
    ? "erro"
    : consultas.every((consulta) => consulta === "success")
      ? "ok"
      : "carregando";
  const pronto = status === "ok";

  const condicoes = useMemo(() => {
    if (!pronto || !periodo) return SEM_CONDICOES;

    const dados = readTiposObra(tipos);
    return avaliarCondicoesObra({
      limitesClasse: limites.data,
      terminoContrato: informacoes.data?.terminoContrato || null,
      periodo,
      codigosTiposObra: [
        ...dados.tipos,
        ...dados.centrosAdicionais.flatMap((adicional) => adicional.tipos),
      ].map((tipo) => tipo.codigo),
      hoje,
    });
  }, [pronto, periodo, tipos, limites.data, informacoes.data, hoje]);

  return { status, pronto, periodo, hoje, condicoes };
}
